/**
 * DISPONIBILIDAD DE PLANIFICACIÓN DE UN OPERARIO — FASE 6.3 §G/§H
 *
 * Patrón semanal sobre `disponibilidad_planificacion_operario`
 * (`GET/PUT /api/operarios/:id/disponibilidad`, FASE 6.1 §F): el PUT
 * sustituye el patrón COMPLETO, así que «guardar» siempre manda la lista
 * entera, nunca un intervalo suelto.
 *
 * Nunca se usan aquí las palabras «jornada laboral», «horas contratadas»
 * ni «fichaje» — esto es disponibilidad de planificación, no una
 * declaración laboral. El solapamiento entre intervalos del mismo día lo
 * rechaza la propia base de datos (`EXCLUDE`, 0048); esta pantalla no
 * repite esa regla en JavaScript, sólo muestra el mensaje que ya
 * devuelve la API cuando ocurre — «LA UI NO DECIDE NADA» (api.ts).
 */
import { useState, type ReactNode } from 'react';

import { api, type IntervaloDisponibilidad, type IntervaloDisponibilidadGuardado } from '../api.ts';
import { Error as ErrorCaja, Panel, useDatos } from './base.tsx';

const DIAS_SEMANA: ReadonlyArray<{ iso: number; nombre: string }> = [
  { iso: 1, nombre: 'Lunes' },
  { iso: 2, nombre: 'Martes' },
  { iso: 3, nombre: 'Miércoles' },
  { iso: 4, nombre: 'Jueves' },
  { iso: 5, nombre: 'Viernes' },
  { iso: 6, nombre: 'Sábado' },
  { iso: 7, nombre: 'Domingo' },
];

interface FilaIntervalo {
  readonly clave: number;
  diaSemana: number;
  horaInicio: string;
  horaFin: string;
}

let contadorClave = 0;

export function DisponibilidadOperario({ idOperario, puedeEditar }: { idOperario: string; puedeEditar: boolean }): ReactNode {
  const disponibilidad = useDatos(
    () => api.get<{ intervalos: IntervaloDisponibilidadGuardado[] }>(`/api/operarios/${idOperario}/disponibilidad`),
    [idOperario],
  );

  return (
    <div className="panel">
      <div className="panel-cabecera">
        <h2>Disponibilidad de planificación</h2>
      </div>
      <div className="panel-cuerpo">
        <Panel estado={disponibilidad}>
          {(d) => <EditorDisponibilidad idOperario={idOperario} puedeEditar={puedeEditar} guardados={d.intervalos} onGuardado={disponibilidad.recargar} />}
        </Panel>
      </div>
    </div>
  );
}

function aFilas(guardados: readonly IntervaloDisponibilidad[]): FilaIntervalo[] {
  return guardados.map((iv) => ({ clave: ++contadorClave, diaSemana: iv.diaSemana, horaInicio: iv.horaInicio.slice(0, 5), horaFin: iv.horaFin.slice(0, 5) }));
}

function EditorDisponibilidad({
  idOperario,
  puedeEditar,
  guardados,
  onGuardado,
}: {
  idOperario: string;
  puedeEditar: boolean;
  guardados: readonly IntervaloDisponibilidadGuardado[];
  onGuardado: () => void;
}): ReactNode {
  //  Inicializador perezoso: sólo calcula `aFilas` una vez, al montar. Un
  //  `useEffect` que resincronizara con `guardados` en cada cambio volvería
  //  a ejecutarse justo después del propio montaje (los efectos siempre
  //  corren tras el primer commit) y regeneraría las claves de `aFilas`
  //  una segunda vez, remontando filas que ya estaban bien. Tras guardar
  //  con éxito, `guardar()` ya resincroniza `filas` explícitamente con la
  //  respuesta del PUT — no hace falta ningún otro mecanismo.
  const [filas, setFilas] = useState<FilaIntervalo[]>(() => aFilas(guardados));
  const [guardando, setGuardando] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const [mensaje, setMensaje] = useState<string | null>(null);

  const errorFormato = filas.some((f) => f.horaInicio >= f.horaFin);

  function anadirIntervalo(dia: number): void {
    setFilas((fs) => [...fs, { clave: ++contadorClave, diaSemana: dia, horaInicio: '08:00', horaFin: '13:00' }]);
    setMensaje(null);
  }
  function quitarIntervalo(clave: number): void {
    setFilas((fs) => fs.filter((f) => f.clave !== clave));
    setMensaje(null);
  }
  function cambiarIntervalo(clave: number, cambios: Partial<Pick<FilaIntervalo, 'horaInicio' | 'horaFin'>>): void {
    setFilas((fs) => fs.map((f) => (f.clave === clave ? { ...f, ...cambios } : f)));
    setMensaje(null);
  }

  async function guardar(): Promise<void> {
    setGuardando(true);
    setError(null);
    setMensaje(null);
    try {
      const r = await api.put<{ intervalos: IntervaloDisponibilidadGuardado[] }>(`/api/operarios/${idOperario}/disponibilidad`, {
        intervalos: filas.map((f) => ({ diaSemana: f.diaSemana, horaInicio: f.horaInicio, horaFin: f.horaFin })),
      });
      setFilas(aFilas(r.intervalos));
      setMensaje('Disponibilidad guardada.');
      onGuardado();
    } catch (err) {
      setError(err);
    } finally {
      setGuardando(false);
    }
  }

  return (
    <div>
      {mensaje !== null && (
        <div className="nota exito" role="status" style={{ marginBottom: 12 }}>
          {mensaje}
        </div>
      )}
      {DIAS_SEMANA.map((d) => {
        const delDia = filas.filter((f) => f.diaSemana === d.iso);
        return (
          <div className="disponibilidad-dia" key={d.iso}>
            <div className="disponibilidad-dia-nombre">{d.nombre}</div>
            <div className="disponibilidad-intervalos">
              {delDia.length === 0 && <span className="suave pequena">Sin disponibilidad declarada.</span>}
              {delDia.map((f) => (
                <div className="disponibilidad-intervalo" key={f.clave}>
                  <input
                    type="time"
                    value={f.horaInicio}
                    onChange={(e) => cambiarIntervalo(f.clave, { horaInicio: e.target.value })}
                    disabled={!puedeEditar || guardando}
                    aria-label={`${d.nombre}: hora de inicio`}
                  />
                  <span aria-hidden="true">–</span>
                  <input
                    type="time"
                    value={f.horaFin}
                    onChange={(e) => cambiarIntervalo(f.clave, { horaFin: e.target.value })}
                    disabled={!puedeEditar || guardando}
                    aria-label={`${d.nombre}: hora de fin`}
                  />
                  {f.horaInicio >= f.horaFin && <span className="pequena" style={{ color: 'var(--error)' }}>La hora de inicio debe ser anterior a la de fin.</span>}
                  {puedeEditar && (
                    <button type="button" className="pequeno enlace" onClick={() => quitarIntervalo(f.clave)} disabled={guardando}>
                      Quitar
                    </button>
                  )}
                </div>
              ))}
              {puedeEditar && (
                <button type="button" className="pequeno" onClick={() => anadirIntervalo(d.iso)} disabled={guardando} style={{ alignSelf: 'flex-start' }}>
                  + Añadir intervalo
                </button>
              )}
            </div>
          </div>
        );
      })}

      {error !== null && <ErrorCaja error={error} />}
      {puedeEditar && (
        <div className="fila-botones" style={{ marginTop: 12 }}>
          <button type="button" className="principal" onClick={() => { void guardar(); }} disabled={guardando || errorFormato}>
            {guardando ? 'Guardando…' : 'Guardar disponibilidad'}
          </button>
        </div>
      )}
    </div>
  );
}
